import { useCallback, useEffect } from 'react';

import { Router } from 'next/router';

import { useBasket } from '~/features/basket/hooks/useBasket';
import { useRaptorEnvironment } from '~/features/raptor/hooks/useRaptorEnvironment';
import { trackRaptorDirectPageView } from '~/features/raptor/services';
import { useCookieConsent } from '~/shared/components/CookieConsent/hooks/useCookieConsent';
import { useFrame } from '~/shared/utils/frame/hooks/useFrame';
import { useMarket } from '~/shared/utils/market/hooks/useMarket';
import { isSSR } from '~/shared/utils/platform/utils/platform';
import { usePage } from '~/templates/pages/hooks/usePage';

let LOADED = false;

export const useRaptorPageView = () => {
	const page = usePage();
	const { market, culture } = useMarket();
	const { data: frame } = useFrame();
	const { allowMarketing, updated } = useCookieConsent();
	const { data: basket } = useBasket();
	const { cookieId, sessionId, reaId, ruid, setRuid } = useRaptorEnvironment();

	const handleRouteChangeComplete = useCallback(() => {
		// Check if raptorTrackingUrl is configured in frame settings
		const raptorTrackingUrl = frame.settings?.raptorTrackingUrl;
		if (!raptorTrackingUrl) {
			return;
		}

		// Skip tracking on server side.
		if (isSSR) {
			return;
		}

		// Get current URL
		const currentUrl = window.location.href;

		// Prepare basic parameters
		const trackingParams = {
			raptorTrackingUrl,
			pageId: page.id,
			language: culture,
			market,
			moduleIds: page.pageElements.map((item) => item.id),
			currentUrl,
		};

		if (basket?.ruId && basket.ruId !== ruid) setRuid(basket.ruId);

		// Add marketing consent parameters if user has given marketing consent
		if (allowMarketing) {
			trackRaptorDirectPageView({
				...trackingParams,
				ruid: basket?.ruId || ruid || undefined,
				reaid: reaId,
				sessionId: sessionId,
				cookieId: cookieId,
			});
		} else {
			// Track without marketing parameters
			trackRaptorDirectPageView(trackingParams);
		}
	}, [frame.settings?.raptorTrackingUrl, page.id, page.pageElements, culture, market, basket?.ruId, ruid, setRuid, allowMarketing, reaId, sessionId, cookieId]);

	useEffect(() => {
		Router.events.on('routeChangeComplete', handleRouteChangeComplete);

		return () => Router.events.off('routeChangeComplete', handleRouteChangeComplete);
	}, [handleRouteChangeComplete]);

	useEffect(() => {
		// We'll only track the first page view if...
		if (
			// ... we haven't already, and...
			!LOADED &&
			// ... cookie information has been loaded, and ...
			updated &&
			// ... we have a session ID, and ...
			sessionId &&
			// ... either marketing is not allowed, in which case we don't need the cookie ID anyway, or it is, and we ALSO have a cookie ID.
			(!allowMarketing || cookieId)
		) {
			handleRouteChangeComplete();
			LOADED = true;
		}
	}, [allowMarketing, cookieId, handleRouteChangeComplete, sessionId, updated]);
};
